import { cva } from 'class-variance-authority';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { IArticle, SimpleColor } from '@boss/types/b2b-b2c';
import { PriceNote, Skeleton } from '@boss/ui';

import PrimaryArticleTable from './Primary';
import SecondaryArticleTable from './Secondary';
import { usePrice } from '../../hooks';
import type { PdpStockLineStatus } from '../../hooks/use-pdp-stock/use-pdp-stock';
import { productPanelVariant } from '../../utils';

export type ArticleValues = {
  quantity: number;
  unit: string;
  productImageSrc?: string;
  lineId?: string;
  price?: number;
  stockproductnumber?: string;
  discount?: number;
};
export type ArticleIdsObject = Record<string, ArticleValues>;

// [46231] sv04: per-line stock status, keyed by article id and threaded from
// ProductPanelWrapper's usePdpStock result into both table variants.
export type StockStatusByArticleId = Record<string, PdpStockLineStatus>;

const stockDotStyle = cva('h-2 w-2 rounded-full', {
  variants: {
    status: {
      available: 'bg-green',
      insufficient: 'bg-yellow-500',
      unavailable: 'bg-red',
    },
  },
});

const stockTextStyle = cva('', {
  variants: {
    status: {
      available: 'text-green',
      insufficient: 'text-yellow-500',
      unavailable: 'text-red',
    },
  },
});

type StockStatusLineProps = PdpStockLineStatus & {
  className?: string;
};

/**
 * [46231] sv04 shared inline stock status (dot + label), rendered under each
 * quantity row in both table variants. A `null` status renders nothing, a
 * loading line holds its place with a skeleton. The label comes from
 * usePdpStock (with the productPanel.stock.availability.* fallback), so the
 * status is never colour-only.
 */
export const StockStatusLine = ({ className, isLoading, label, status }: StockStatusLineProps) => {
  const { t } = useTranslation('product');

  if (isLoading) {
    return <Skeleton className="w-30 h-4" />;
  }

  if (!status) {
    return null;
  }

  return (
    <div className={twMerge('small flex items-center gap-2', className)} role="status">
      <span aria-hidden="true" className={stockDotStyle({ status })} />
      <span className={stockTextStyle({ status })}>{label ?? t(`productPanel.stock.availability.${status}`)}</span>
    </div>
  );
};

export type Props = {
  articles?: IArticle[];
  articleType: 'paint' | 'nonPaint';
  selectedArticleIdsObject: ArticleIdsObject;
  setSelectedArticleIdsObject: Dispatch<SetStateAction<ArticleIdsObject>>;
  showPrivatePrices?: boolean;
  togglePrivatePrices?: () => void;
  type?: 'default' | 'slim';
  isLoading?: boolean;
  variant?: 'primary' | 'secondary';
  isLoggedIn?: boolean;
  selectedColor?: SimpleColor;
  stockStatusByArticleId?: StockStatusByArticleId;
  // [39787] RBAC (B2B only). Default true so colora/B2C is unaffected.
  showprice?: boolean;
  canorder?: boolean;
};

const ArticlesTable = ({
  variant,
  togglePrivatePrices,
  showPrivatePrices,
  setSelectedArticleIdsObject,
  showprice = true,
  ...props
}: Props) => {
  const { t } = useTranslation(['common', 'product']);
  const { showPrice } = usePrice();

  const updateSelectedArticlesIdsObject = (id: string, newObject: ArticleValues) => {
    setSelectedArticleIdsObject(idsObject => {
      const { [id]: originalObject, ...rest } = idsObject;
      const mergedObject = { ...originalObject, ...newObject };

      return { ...rest, [id]: mergedObject };
    });
  };

  const commonProps = {
    showPrivatePrices,
    setSelectedArticleIdsObject,
    updateSelectedArticlesIdsObject,
    showprice,
  };

  return (
    <>
      {productPanelVariant === 'primary' ? (
        <PrimaryArticleTable {...props} {...commonProps} />
      ) : (
        <SecondaryArticleTable {...props} {...commonProps} />
      )}
      {togglePrivatePrices && showPrice && showprice !== false && (
        <div className="relative">
          <PriceNote
            active={!!showPrivatePrices}
            onClick={togglePrivatePrices}
            translations={t('productPanel.priceNote', { returnObjects: true, ns: 'product' })}
          />
          <div className="right-1/20 border-l-10 border-r-10 border-b-10 border-b-blue-light absolute bottom-full h-0 w-0 border-l-transparent border-r-transparent" />
        </div>
      )}
    </>
  );
};

export default ArticlesTable;
